import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Target, Clock, Eye, ArrowCounterClockwise, Bookmark, Plus, Trash, PencilSimple, Check, X } from "@phosphor-icons/react";

const REFRESH_OPTIONS = [10, 30, 60, 120];

export default function WallboardSettings({
  open, onClose,
  config, update, resetActive,
  activePreset, presetNames,
  selectPreset, createPreset, renamePreset, deletePreset,
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="wallboard-settings">
        <DialogHeader>
          <DialogTitle className="font-heading">Wallboard display</DialogTitle>
          <p className="text-xs text-zinc-500 mt-1">
            Settings are saved per browser. Pick a preset below or create your own (e.g. one per shop TV).
          </p>
        </DialogHeader>

        <div className="space-y-5 py-2 max-h-[65vh] overflow-y-auto pr-1">
          <PresetSection
            activePreset={activePreset}
            presetNames={presetNames}
            selectPreset={selectPreset}
            createPreset={createPreset}
            renamePreset={renamePreset}
            deletePreset={deletePreset}
          />

          {/* Daily goal */}
          <Section icon={Target} title="Daily goal">
            <ToggleRow
              label="Show daily scan goal"
              checked={config.goalEnabled}
              onChange={(v) => update({ goalEnabled: v })}
              testId="toggle-goal"
            />
            {config.goalEnabled && (
              <div className="flex items-center gap-3 mt-3">
                <label className="text-sm font-semibold w-32">Target scans</label>
                <Input
                  type="number"
                  min={1}
                  value={config.dailyGoal}
                  onChange={(e) => update({ dailyGoal: Math.max(1, Number(e.target.value) || 0) })}
                  className="w-32 h-9"
                  data-testid="input-daily-goal"
                />
                <span className="text-xs text-zinc-500">scans / day</span>
              </div>
            )}
          </Section>

          {/* Refresh */}
          <Section icon={Clock} title="Auto-refresh">
            <div className="flex flex-wrap items-center gap-2">
              {REFRESH_OPTIONS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => update({ refreshSec: s })}
                  data-testid={`refresh-${s}`}
                  className={`px-3 py-1.5 rounded text-xs font-bold uppercase tracking-wider transition-colors ${
                    config.refreshSec === s
                      ? "bg-[#C8102E] text-white"
                      : "bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200"
                  }`}
                >
                  {s < 60 ? `${s}s` : `${s / 60}m`}
                </button>
              ))}
            </div>
          </Section>

          {/* Panels */}
          <Section icon={Eye} title="Panels">
            <div className="grid grid-cols-2 gap-y-2 gap-x-4">
              <ToggleRow label="Live clock" checked={config.showClock} onChange={(v) => update({ showClock: v })} testId="toggle-clock" />
              <ToggleRow label="Headline KPIs" checked={config.showKpis} onChange={(v) => update({ showKpis: v })} testId="toggle-kpis" />
              <ToggleRow label="Today's fulfillment" checked={config.showFulfillmentTile !== false} onChange={(v) => update({ showFulfillmentTile: v })} testId="toggle-fulfillment" />
              <ToggleRow label="Stage tiles" checked={config.showStageTiles} onChange={(v) => update({ showStageTiles: v })} testId="toggle-stage" />
              <ToggleRow label="Pipeline bar" checked={config.showPipeline} onChange={(v) => update({ showPipeline: v })} testId="toggle-pipeline" />
              <ToggleRow label="Today's leaderboard" checked={config.showLeaderboard} onChange={(v) => update({ showLeaderboard: v })} testId="toggle-leaderboard" />
              <ToggleRow label="Activity feed" checked={config.showTicker} onChange={(v) => update({ showTicker: v })} testId="toggle-ticker" />
            </div>
          </Section>
        </div>

        <DialogFooter className="flex items-center justify-between gap-2">
          <Button variant="ghost" onClick={resetActive} data-testid="reset-wallboard-config" className="text-zinc-500">
            <ArrowCounterClockwise size={14} weight="bold" className="mr-1.5" />
            Reset this preset
          </Button>
          <Button onClick={onClose} className="bg-[#C8102E] hover:bg-[#A00D25]" data-testid="close-wallboard-settings">
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PresetSection({ activePreset, presetNames, selectPreset, createPreset, renamePreset, deletePreset }) {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [renaming, setRenaming] = useState(null); // name being renamed
  const [renameValue, setRenameValue] = useState("");

  const submitCreate = () => {
    if (createPreset(newName)) {
      setNewName(""); setCreating(false);
    }
  };
  const submitRename = () => {
    if (renamePreset(renaming, renameValue)) {
      setRenaming(null); setRenameValue("");
    }
  };

  return (
    <Section icon={Bookmark} title="Preset">
      <div className="flex flex-wrap gap-1.5" data-testid="preset-list">
        {presetNames.map((name) => {
          const active = name === activePreset;
          const isRenaming = renaming === name;
          if (isRenaming) {
            return (
              <div key={name} className="flex items-center gap-1 bg-zinc-100 dark:bg-zinc-800 rounded px-1.5 py-1">
                <Input
                  autoFocus
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") submitRename(); if (e.key === "Escape") setRenaming(null); }}
                  className="h-7 w-32 text-xs"
                  data-testid={`rename-input-${name}`}
                />
                <button onClick={submitRename} data-testid={`rename-save-${name}`} className="text-emerald-600 hover:text-emerald-500"><Check size={14} weight="bold" /></button>
                <button onClick={() => setRenaming(null)} className="text-zinc-500 hover:text-zinc-300"><X size={14} weight="bold" /></button>
              </div>
            );
          }
          return (
            <div key={name} className={`group flex items-center rounded overflow-hidden border transition-colors ${
              active ? "border-[#C8102E] bg-[#C8102E] text-white" : "border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-300"
            }`}>
              <button
                type="button"
                onClick={() => selectPreset(name)}
                data-testid={`preset-${name}`}
                className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider"
              >
                {name}
              </button>
              {active && (
                <div className="flex items-center gap-0.5 pr-1.5">
                  <button
                    type="button"
                    onClick={() => { setRenaming(name); setRenameValue(name); }}
                    data-testid={`rename-${name}`}
                    className="w-6 h-6 flex items-center justify-center hover:bg-white/20 rounded"
                    title="Rename"
                  >
                    <PencilSimple size={12} weight="bold" />
                  </button>
                  {presetNames.length > 1 && (
                    <PresetDeleteButton onConfirm={() => deletePreset(name)} testId={`delete-${name}`} />
                  )}
                </div>
              )}
            </div>
          );
        })}

        {creating ? (
          <div className="flex items-center gap-1 bg-zinc-100 dark:bg-zinc-800 rounded px-1.5 py-1">
            <Input
              autoFocus
              placeholder="New preset name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submitCreate(); if (e.key === "Escape") setCreating(false); }}
              className="h-7 w-40 text-xs"
              data-testid="new-preset-input"
            />
            <button onClick={submitCreate} data-testid="new-preset-save" className="text-emerald-600 hover:text-emerald-500"><Check size={14} weight="bold" /></button>
            <button onClick={() => { setCreating(false); setNewName(""); }} className="text-zinc-500 hover:text-zinc-300"><X size={14} weight="bold" /></button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setCreating(true)}
            data-testid="new-preset-btn"
            className="px-2.5 py-1.5 text-xs font-bold uppercase tracking-wider border border-dashed border-zinc-300 dark:border-zinc-600 text-zinc-500 hover:text-zinc-900 dark:hover:text-white hover:border-zinc-500 rounded inline-flex items-center gap-1"
          >
            <Plus size={12} weight="bold" /> New
          </button>
        )}
      </div>
      <p className="text-[10px] text-zinc-500 mt-2 leading-relaxed">
        Edits below save to <span className="font-bold uppercase tracking-widest text-[#C8102E]">{activePreset}</span>. Create new presets for each shop TV.
      </p>
    </Section>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} weight="bold" className="text-[#C8102E]" />
        <h3 className="font-heading font-black uppercase tracking-[0.2em] text-xs">{title}</h3>
      </div>
      <div className="pl-6">{children}</div>
    </div>
  );
}

function ToggleRow({ label, checked, onChange, testId }) {
  return (
    <label className="flex items-center gap-2.5 text-sm cursor-pointer select-none">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        data-testid={testId}
        className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${
          checked ? "bg-[#C8102E]" : "bg-zinc-300 dark:bg-zinc-700"
        }`}
      >
        <span
          className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
            checked ? "translate-x-4" : "translate-x-0.5"
          }`}
        />
      </button>
      <span className="font-medium">{label}</span>
    </label>
  );
}


// Lightweight 2-click confirm button — used here instead of window.confirm()
// because that's silently blocked when the page runs in a sandboxed iframe.
// Click once → button turns red and label changes to "Confirm?"; click again
// within 3 seconds to actually delete. Auto-reverts otherwise.
function PresetDeleteButton({ onConfirm, testId }) {
  const [armed, setArmed] = useState(false);
  React.useEffect(() => {
    if (!armed) return undefined;
    const t = setTimeout(() => setArmed(false), 3000);
    return () => clearTimeout(t);
  }, [armed]);
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        if (armed) { setArmed(false); onConfirm(); }
        else setArmed(true);
      }}
      data-testid={testId}
      title={armed ? "Click again to confirm" : "Delete"}
      className={`flex items-center justify-center rounded transition-colors ${
        armed
          ? "h-6 px-1.5 bg-[#C8102E] text-white font-bold text-[10px]"
          : "w-6 h-6 hover:bg-white/20"
      }`}
    >
      {armed ? "CONFIRM?" : <Trash size={12} weight="bold" />}
    </button>
  );
}
